import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { formatDateOnly } from "../../utils/formatDateOnly";
import "../../styles/Form.css";
import "../../styles/JobPreview.css";

export default function JobPreview({ job }) {
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (token) {
      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/api/apply/check`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setApplication(res.data))
        .catch(() => {});
    }
  }, [token]);

  if (!job) return null;

  const {
    p_id,
    p_position_title,
    p_position_number,
    p_posting = {},
    p_landing_page_desc,
  } = job;

  const closing = formatDateOnly(job.p_closing_date ?? p_posting?.p_closing_date);

  const handleApplyClick = async () => {
    Cookies.set("pendingJobId", p_id, { path: "/", expires: 1 });

    if (!token) {
      navigate(`/register?redirect=/apply/${p_id}`);
      return;
    }

    if (application?.pipeline_status === 2) {
      navigate(`/candidate/onboarding/${application.application_id}`);
      return;
    }

    if (application?.pipeline_status === 3) {
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/apply/selection`,
        { job_id: p_id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (err) {
      console.error("Failed to persist selected job:", err);
    }

    navigate(`/apply/${p_id}`);
  };

  const renderButtonLabel = () => {
    if (application?.pipeline_status === 3) return "Completed";
    if (application?.pipeline_status === 2) return "Continue Application";
    return "Apply Now";
  };

  return (
    <div className="form-wrapper slide-wide jobp-wrapper">
      <div className="section mb-4">
        <div className="section-title">Position Description</div>
        <div className="section-content">
          <h5 className="fw-bold mb-3">{p_position_title || "Job Title"}</h5>
          <ul className="list-unstyled small text-muted mb-4">
            <li>
              <strong>Position #:</strong> {p_position_number || "—"}
            </li>

            {job.p_visible_info?.openings && (
              <li>
                <strong>Openings:</strong>{" "}
                {job.p_openings ?? job.p_posting?.p_num_openings ?? "—"}
              </li>
            )}
            {job.p_visible_info?.work_type && (
              <li>
                <strong>Type:</strong>{" "}
                {[
                  job.p_landing_page_sell?.position_type,
                  job.p_landing_page_sell?.driver_type,
                ]
                  .filter(Boolean)
                  .join(" - ") || "—"}
              </li>
            )}

            {job.p_visible_info?.closing_date && (
              <li>
                <strong>Closing:</strong> {closing}
              </li>
            )}
            {job.p_visible_info?.dangerous_goods && (
              <li>
                <strong>Dangerous Goods:</strong> {job.p_dangerous_goods ?? "—"}
              </li>
            )}
            {job.p_visible_info?.license_classes && (
              <li>
                <strong>License Classes:</strong>{" "}
                {Array.isArray(job.p_landing_page_sell?.license_class)
                  ? job.p_landing_page_sell.license_class.join(", ")
                  : job.p_landing_page_sell?.license_class || "—"}
              </li>
            )}

            {job.p_visible_info?.license_conditions && (
              <li>
                <strong>License Conditions:</strong>{" "}
                {Array.isArray(job.p_landing_page_sell?.license_conditions)
                  ? job.p_landing_page_sell.license_conditions.join(", ")
                  : job.p_landing_page_sell?.license_conditions || "—"}
              </li>
            )}
            {job.p_visible_info?.route && (
              <li>
                <strong>Route:</strong> {job.p_route_description ?? "—"}
              </li>
            )}
          </ul>

          {p_landing_page_desc && (
            <div
              className="job-description"
              dangerouslySetInnerHTML={{ __html: p_landing_page_desc }}
            />
          )}
        </div>
      </div>

      <div className="jobp-footer">
        <button
          onClick={handleApplyClick}
          disabled={application?.pipeline_status === 3}
          className={
            application?.pipeline_status === 3
              ? "btn btn-completed"
              : application?.pipeline_status === 2
                ? "btn btn-warning"
                : "btn btn-primary"
          }
        >
          {renderButtonLabel()}
        </button>
      </div>
    </div>
  );
}
